import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, AlertTriangle, MinusCircle, ChevronRight } from "lucide-react";

/**
 * Lista de implantação por etapa — a MESMA para o cliente e para o time OPLYX
 * (`/api/onboarding/readiness` e `/api/admin/implantacao/{id}`).
 * No painel (`admin`), cada item pendente ganha "Feito pela OPLYX" / "Não se aplica".
 */

export const STATE_CFG = {
  travado:     { label: "Travado",            cls: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300" },
  implantando: { label: "Implantando",        cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300" },
  pronto:      { label: "Pronto para operar", cls: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300" },
  operando:    { label: "Operando",           cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" },
};

const ICON = {
  ok:       <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" strokeWidth={1.75} />,
  pendente: <Circle className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.75} />,
  problema: <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" strokeWidth={1.75} />,
  na:       <MinusCircle className="w-4 h-4 text-muted-foreground/60 shrink-0" strokeWidth={1.75} />,
};

export function ReadinessSummary({ r, compact = false }) {
  const st = STATE_CFG[r.state] || STATE_CFG.implantando;
  return (
    <div className="space-y-2" data-testid="readiness-summary">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-xs font-semibold ${st.cls}`}>
            {st.label}{r.state === "travado" ? ` há ${r.stuck_days} dia(s)` : ""}
          </span>
          <span className="text-sm text-muted-foreground">
            {r.required_done} de {r.required_total} itens obrigatórios
            {r.current_stage_label && r.state !== "pronto" && r.state !== "operando" && ` · etapa atual: ${r.current_stage_label}`}
          </span>
        </div>
        <span className="text-sm font-semibold tabular-nums">{r.pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${r.pct}%` }} />
      </div>
      {!compact && r.problems?.length > 0 && (
        <div className="text-xs text-rose-700 dark:text-rose-300">
          {r.problems.length} problema(s): {r.problems.map(p => p.label).join(" · ")}
        </div>
      )}
    </div>
  );
}

function OverrideControls({ item, onOverride }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  if (item.override) {
    return (
      <button type="button" onClick={() => onOverride(item, null, "")}
              className="text-[11px] text-muted-foreground underline hover:text-foreground">desfazer marcação</button>
    );
  }
  if (item.status === "ok" || item.status === "na") return null;
  if (asking) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Por que não se aplica?"
               className="h-7 w-56 rounded-md border border-border bg-background px-2 text-xs" autoFocus />
        <button type="button" disabled={!reason.trim()} onClick={() => { onOverride(item, "nao_se_aplica", reason); setAsking(false); }}
                className="text-[11px] font-medium hover:underline disabled:opacity-40">salvar</button>
        <button type="button" onClick={() => setAsking(false)} className="text-[11px] text-muted-foreground hover:underline">cancelar</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 mt-1">
      <button type="button" onClick={() => onOverride(item, "feito_oplyx", "")}
              className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 hover:underline">Feito pela OPLYX</button>
      <button type="button" onClick={() => setAsking(true)}
              className="text-[11px] font-medium text-muted-foreground hover:text-foreground hover:underline">Não se aplica</button>
    </div>
  );
}

/**
 * `inline`: { [item_key]: (onDone) => JSX } — itens resolvidos na própria
 * Implantação (sem pular para outra tela). `openKey` abre um deles já de cara
 * (deep link `?item=`). `onResolved` recarrega a lista depois de salvar.
 */
/** Instruções para quem resolve um item fora do OPLYX (ex.: portal da SEFAZ). */
function ItemHelp({ help }) {
  return (
    <details className="mt-1.5 text-xs" data-testid="readiness-item-help">
      <summary className="cursor-pointer font-medium text-sky-700 dark:text-sky-400">Como fazer e como conferir</summary>
      <div className="mt-1.5 space-y-1.5 rounded-md border border-border bg-muted/30 p-2.5">
        {help.vincular && <div><span className="font-medium">Fazer:</span> {help.vincular}</div>}
        {help.conferir && <div><span className="font-medium">Conferir:</span> {help.conferir}</div>}
        <div className="flex gap-3 flex-wrap pt-0.5">
          {help.consulta_url && <a href={help.consulta_url} target="_blank" rel="noreferrer" className="underline">Abrir a consulta ↗</a>}
          {help.sistema_url && <a href={help.sistema_url} target="_blank" rel="noreferrer" className="underline">Abrir o sistema ↗</a>}
          {help.manual_url && <a href={help.manual_url} target="_blank" rel="noreferrer" className="underline">Manual oficial ↗</a>}
        </div>
      </div>
    </details>
  );
}

export default function ReadinessStages({ stages, admin = false, onOverride, inline = {}, openKey = null, onResolved,
                                          adminActions = {} }) {
  const [open, setOpen] = useState(openKey);
  const pending = (it) => it.status === "pendente" || it.status === "problema";
  return (
    <div className="space-y-4" data-testid="readiness-stages">
      {stages.map(stage => (
        <div key={stage.key} className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="text-sm font-semibold">{stage.label}</div>
            <div className="text-xs text-muted-foreground tabular-nums">{stage.done}/{stage.total}</div>
          </div>
          <ul className="divide-y divide-border">
            {stage.items.map(it => (
              <li key={it.key} className="px-4 py-2.5" data-testid={`readiness-item-${it.key}`}
                  ref={el => { if (el && openKey === it.key && open === it.key) el.scrollIntoView({ block: "center" }); }}>
                <div className="flex items-start gap-3">
                <span className="mt-0.5">{ICON[it.status]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm ${it.status === "na" ? "text-muted-foreground" : ""}`}>{it.label}</span>
                    {it.required && it.status !== "ok" && it.status !== "na" && (
                      <span className="rounded px-1.5 py-px text-[10px] font-semibold uppercase bg-muted text-muted-foreground">obrigatório</span>
                    )}
                    {it.who === "oplyx" && it.status !== "ok" && it.status !== "na" && (
                      <span className="rounded px-1.5 py-px text-[10px] font-semibold bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300">a OPLYX cuida disso</span>
                    )}
                    {it.who === "contador" && it.status !== "ok" && it.status !== "na" && (
                      <span className="rounded px-1.5 py-px text-[10px] font-semibold bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300">com o contador</span>
                    )}
                    {it.override && (
                      <span className="rounded px-1.5 py-px text-[10px] font-semibold bg-muted text-muted-foreground">
                        {it.override.label}{it.override.reason ? `: ${it.override.reason}` : ""}
                      </span>
                    )}
                  </div>
                  {it.evidence && <div className="text-xs text-muted-foreground mt-0.5">{it.evidence}</div>}
                  {(it.status === "pendente" || it.status === "problema") && it.impact && (
                    <div className="text-xs text-muted-foreground mt-0.5"><span className="font-medium">Se faltar:</span> {it.impact}</div>
                  )}
                  {admin && it.help && <ItemHelp help={it.help} />}
                  {admin && adminActions[it.key] && adminActions[it.key](it)}
                  {admin && <OverrideControls item={it} onOverride={onOverride} />}
                </div>
                {!admin && inline[it.key] && (
                  <button type="button" onClick={() => setOpen(open === it.key ? null : it.key)}
                          data-testid={`readiness-inline-toggle-${it.key}`}
                          className="shrink-0 inline-flex items-center gap-0.5 text-xs font-medium hover:underline">
                    {open === it.key ? "Fechar" : pending(it) ? "Resolver aqui" : "Alterar"}
                    <ChevronRight className={`w-3 h-3 transition-transform ${open === it.key ? "rotate-90" : ""}`} />
                  </button>
                )}
                {!admin && !inline[it.key] && it.to && pending(it) && it.who !== "oplyx" && (
                  <Link to={it.to} className="shrink-0 inline-flex items-center gap-0.5 text-xs font-medium hover:underline">
                    Resolver <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
                {admin && it.to && <span className="shrink-0 text-[11px] text-muted-foreground font-mono">{it.to}</span>}
                </div>
                {!admin && inline[it.key] && open === it.key && (
                  <div className="mt-3 ml-7 rounded-lg border border-border bg-muted/30 p-3">
                    {inline[it.key](() => { setOpen(null); onResolved && onResolved(); })}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
